import React from 'react';
import {
  createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation, useRouteError,
} from 'react-router-dom';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DepartmentProvider, useDepartment } from './contexts/DepartmentContext';
import { PageHeaderProvider } from './contexts/PageHeaderContext';
import { ToastProvider } from './contexts/ToastContext';
import NewOrder from './pages/NewOrder';
import MyOrders from './pages/MyOrders';
import MyDashboard from './pages/MyDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettings from './pages/AdminSettings';
import OrderPage from './pages/OrderPage';
import MySettings from './pages/MySettings';
import Approvals from './pages/Approvals';
import DepartmentOrders from './pages/DepartmentOrders';
import DepartmentDashboard from './pages/DepartmentDashboard';
import MyHires from './pages/MyHires';
import DepartmentHires from './pages/DepartmentHires';
import AdminHires from './pages/AdminHires';
import AdminAnalytics from './pages/AdminAnalytics';
import BudgetOverview from './pages/BudgetOverview';
import BudgetCostCodes from './pages/BudgetCostCodes';
import BudgetComparePopout from './pages/BudgetComparePopout';
import MarketplaceBrowse from './pages/MarketplaceBrowse';
import MarketplaceListingDetail from './pages/MarketplaceListingDetail';
import MarketplaceMyListings from './pages/MarketplaceMyListings';
import MarketplaceCreateListing from './pages/MarketplaceCreateListing';
import MyTasks from './pages/MyTasks';
import GagBreakdown from './pages/GagBreakdown';
import RiskAssessmentLibrary from './pages/RiskAssessmentLibrary';
import RiskAssessmentBlockEditor from './pages/RiskAssessmentBlockEditor';
import RiskAssessmentTemplateEditor from './pages/RiskAssessmentTemplateEditor';
import RiskAssessmentOverview from './pages/RiskAssessmentOverview';
import RiskAssessmentBuilder from './pages/RiskAssessmentBuilder';
import RiskAssessmentDetail from './pages/RiskAssessmentDetail';
import Grading from './pages/Grading';
import Timesheets from './pages/Timesheets';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import VerifyEmail from './pages/VerifyEmail';
import GradingReferenceApproval from './pages/GradingReferenceApproval';
import OnboardingCreateProduction from './pages/OnboardingCreateProduction';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookieConsentBanner from './components/CookieConsentBanner';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTopOnPageChange from './components/ScrollToTopOnPageChange';

// Auth guard — redirects to /login if not authenticated
function RequireAuth({ children }) {
  const { authenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!authenticated) {
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  return children;
}

// Redirect away from auth pages if already logged in
function RedirectIfAuthenticated({ children }) {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (authenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Force password change — redirects to /settings if mustChangePassword is true
function RequirePasswordChange({ children }) {
  const { mustChangePassword } = useAuth();
  const location = useLocation();

  if (mustChangePassword && location.pathname !== '/settings') {
    return <Navigate to="/settings" replace />;
  }

  return children;
}

// Smart redirect to dashboard based on role (uses context)
function DashboardRedirect() {
  const { isAdmin, activeRole, loading } = useDepartment();
  const { user } = useAuth();
  // Wait for department context — without this, a buyer logging in could be
  // briefly redirected to /dashboard before activeRole resolves, then get
  // stuck there because <Navigate> fires once per mount.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }
  // Stranded buyer rescue: if the user signed up with intent to create a
  // production but never completed the wizard (e.g. email verified on a
  // different device, localStorage cleared), bounce them into the wizard.
  // Guard with "hasn't already onboarded": a user who is already a buyer of a
  // real department (or an admin) has completed setup — never loop them back,
  // even if their cached user.signup_intent is momentarily stale.
  if (user && user.signup_intent === 'create_production' && !isAdmin && activeRole !== 'buyer') {
    return <Navigate to="/onboarding/new-production" replace />;
  }
  if (isAdmin) return <Navigate to="/admin/dashboard" replace />;
  // Buyers land on the department dashboard — DepartmentRoute accepts them.
  if (activeRole === 'buyer') return <Navigate to="/department/dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

// Admin route guard - only global admins (isAdmin from DepartmentContext)
function AdminRoute({ children }) {
  const { isAdmin, loading } = useDepartment();

  if (loading) return null; // Wait for context to load
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Buyer route guard - buyer role in active department OR global admin OR
// system-level buyer OR anyone holding the department-settings capability in
// the department they are STANDING IN.
//
// THE LAST OPERAND IS THE POINT, and it is a CAPABILITY, never a role name.
// `activeRole === 'supervisor'` would admit a supervisor while they are
// standing in a department where they hold nothing — the any-department flag
// this whole programme removes (CLAUDE.md, "Authority model") — and it would
// lock out a future Coordinator role granted the same column. `capabilities`
// comes from DepartmentContext and is re-derived from the ACTIVE department on
// every switch, so authority stops at the department boundary.
//
// This is the CLIENT half only. Every server gate behind /admin/settings landed
// in earlier commits and is what actually enforces any of this; a bypass of
// this guard buys an attacker a page whose every request still 403s.
//
// ─────────────────────────────────────────────────────────────────────────────
// 🚨 WIDENED 2026-08-10, AFTER IT LOCKED A PAYING CLIENT OUT OF HIS OWN LABEL
// ─────────────────────────────────────────────────────────────────────────────
// The last operand used to be `capabilities?.canManageDepartmentSettings`
// ALONE — the ACTIVE department's set. A supervisor who had created his own
// production label asked *"how can I change the name from applecross to another
// production? It's keeps saying I need to be admin"*. The server admitted him
// TWICE over (creator, and settings rights in a department under the label);
// this guard bounced him to /dashboard before the page ever mounted, and the
// nav item was hidden by the same test, so there was no surface at all.
//
// Two caller classes the old test missed, both admitted by the server:
//   (a) THE CREATOR WHO HOLDS THE CAPABILITY NOWHERE — `ownsProductionLabel`.
//   (b) THE SETTINGS-HOLDER STANDING IN THE WRONG DEPARTMENT (My Space, or a
//       department where they are a plain member) — `holdsDepartmentSettings-
//       Somewhere`. The server scans EVERY department under the label; the
//       active one is at best one of them.
//
// 🔑 THE TWO NEW OPERANDS DECIDE WHETHER A DOOR OPENS, AND NOTHING ELSE. They
// are NOT an any-department authority grant, and the sealed box is intact:
// every request made from behind this door is still resolved PER DEPARTMENT by
// the server, and every control on the page renders from a server-computed
// answer for the row it acts on (`can_edit`, `capabilitiesFor(dept.id)`).
// Authority still stops at the department boundary; only the screens do not.
//
// ⚠️ THEY HAVE THE SHAPE OF THE CARRY-OVER DEFECT TRANCHE 3 EXISTS TO DELETE,
// so they will read as wrong and someone will narrow them back. Narrowing them
// back re-creates this exact lockout. If a page behind this guard ever starts
// ACTING on the strength of it rather than on a per-department answer, that is
// the thing to fix — not this list.
//
// 🔻 THE UNSCOPED `isBuyer` OPERAND IS DELETED (2026-09-23, fix/unscoped-buyer-gates).
// It admitted every self-signup (the wizard writes an unscoped `buyer` row for all
// of them) standing anywhere, and since that branch NO server route behind this
// page honours the flag. Pinned by App.BuyerRoute.test.js.
export function BuyerRoute({ children }) {
  const {
    isAdmin, activeRole, capabilities,
    holdsDepartmentSettingsSomewhere, ownsProductionLabel, loading,
  } = useDepartment();

  if (loading) return null;
  if (isAdmin || activeRole === 'buyer' || !!capabilities?.canManageDepartmentSettings
      || holdsDepartmentSettingsSomewhere || ownsProductionLabel) {
    return children;
  }
  return <Navigate to="/dashboard" replace />;
}

// Budget route guard - admin, buyer, or anyone with can_view_budget permission
function BudgetRoute({ children }) {
  const { isAdmin, isBuyer, canViewBudget, loading } = useDepartment();

  if (loading) return null;
  if (isAdmin || isBuyer || canViewBudget) {
    return children;
  }
  return <Navigate to="/dashboard" replace />;
}

// Department route guard — admit anyone who may VIEW the department's orders in
// the ACTIVE department, the global admin, or (for /department/orders only) a
// Stores-capable user reaching the Deliveries queue.
//
// 🔑 GATE ON THE CAPABILITY, NEVER A ROLE NAME (CLAUDE.md, "Role Checking"). This
// used to test `activeRole === 'buyer' || 'supervisor' || 'sub_department_lead'`,
// which is an any-department shape (it would admit a supervisor STANDING in a
// department where they hold nothing) and would lock out a future Coordinator
// role granted the same column. `capabilities` is the ACTIVE department's resolved
// set (DepartmentContext), re-derived on every department switch, so authority
// stops at the department boundary. `isAdmin ||` is load-bearing — a site admin's
// `capabilities` is `{}`.
//
// 🚨 OWNER RULING 2026-08-15: a plain `user` loses department-orders/-hires access
// (can_view_department_orders is seeded 0 for `user`) and belongs on My Orders, so
// a caller lacking the capability is sent to /orders — not shown a page whose
// every request now 403s. While capabilities are still LOADING, render nothing (no
// redirect flash); redirect only once resolved-and-lacking.
//
// allowStores: /department/orders carries the Deliveries queue, which a
// Stores-capable `user` must still reach — the page + server scope them to their
// delivery-stage orders. /department/hires has NO stores surface (Deliveries is an
// orders page), so it passes no allowStores — deliberately different from the
// orders gate, matching the server (mayViewDeptHires has no canManageStores
// disjunct).
//
// capability: WHICH capability admits, per route — orders/hires gate on
// canViewDepartmentOrders, the dashboard on canViewDepartmentDashboard (matching
// the server's requireUserDashboardAccess / dashboard ring-fence). The seeds
// coincide today (both 0 for `user`, 1 for buyer/supervisor/sub_department_lead)
// but that is a seeds fact, enforced nowhere: a custom role granted one column
// without the other must get exactly the pages its column names — never
// lockout-with-access or a link to a 403.
export function DepartmentRoute({ children, allowStores = false, capability = 'canViewDepartmentOrders' }) {
  const { isAdmin, capabilities, canManageStores, loading } = useDepartment();

  if (loading) return null;
  if (isAdmin || (allowStores && canManageStores) || !!capabilities?.[capability]) {
    return children;
  }
  return <Navigate to="/orders" replace />;
}

// ─────────────────────────────────────────────────────────────────────────────
// THE DATA ROUTER (2026-09-15, R14) — why this is not `<BrowserRouter>` any more
// ─────────────────────────────────────────────────────────────────────────────
// Owner: *"when I start filling out an order and switch to a differnt page the
// order is lost"*, ruling *"Switch the app to the newer router setup, build it
// once as a shared piece and use it everywhere work can be lost"*. The shared
// piece is `hooks/useUnsavedChangesGuard.js`, and the only react-router API
// that can hold a navigation for a confirm dialog — sidebar, header, in-page
// links AND the browser's Back — is `useBlocker`, which exists ONLY inside a
// data router (`createBrowserRouter` + `RouterProvider`) and throws elsewhere.
//
// 🔑 THE ROUTE TREE BELOW IS THE SAME JSX, NOT A ROUTE-OBJECT REWRITE. One splat
// root route renders `AppRoutes`, whose `<Routes>` are the pre-migration tree
// byte-for-byte — react-router's own documented first step for moving an app
// onto `RouterProvider`. Every path, guard, redirect, nesting and the `*`
// fallback is therefore unchanged by construction, and `App.routeParity.test.js`
// pins the path list so a later "convert to objects" cannot drop one silently.
// There is no loader/action, so the router initialises synchronously and
// renders on first paint exactly as `BrowserRouter` did; no scroll restoration
// was added (there was none).
//
// 🚨 `errorElement` RETHROWS, AND WITHOUT IT `ErrorBoundary` GOES DARK. A data
// router wraps its ROOT route in its own render error boundary whether or not
// you give it an `errorElement` (default: react-router's "Unexpected Application
// Error!" page), so a page that throws while rendering would never reach the
// app's `ErrorBoundary` above `RouterProvider` — no "Something went wrong / Try
// Again / Reload Page" card. Rethrowing from the route's error element sends
// the error past the router's boundary to ours. Pinned by §3 of
// `App.dataRouter.test.js`.
//
// ⚠️ THE ROUTER IS CREATED LAZILY, not at module scope: `App.BuyerRoute.test.js`
// and `App.DepartmentRoute.test.js` import this file for its guards, and a
// module-scope `createBrowserRouter` would subscribe to jsdom's history on
// import. One instance for the life of the page — never one per render.
function RethrowRouteError() {
  throw useRouteError();
}

function AppRoutes() {
  return (
      <AuthProvider>
      <ToastProvider>
      <ScrollToTopOnPageChange />
      <Routes>
        {/* Public auth routes — outside Layout and DepartmentProvider */}
        <Route path="/login" element={<RedirectIfAuthenticated><Login /></RedirectIfAuthenticated>} />
        <Route path="/signup" element={<RedirectIfAuthenticated><Signup /></RedirectIfAuthenticated>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/grading-approval" element={<GradingReferenceApproval />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />

        {/* Self-signup buyer onboarding wizard. Auth-required (only a freshly-
            verified user runs it) but rendered outside the Layout/sidebar so
            the experience is full-screen and uncluttered. */}
        <Route path="/onboarding/new-production" element={
          <RequireAuth><OnboardingCreateProduction /></RequireAuth>
        } />

        {/* Protected routes — require authentication */}
        <Route path="/*" element={
          <RequireAuth>
            <DepartmentProvider>
              <RequirePasswordChange>
              <PageHeaderProvider>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<DashboardRedirect />} />
                  <Route path="orders/new" element={<NewOrder />} />
                  <Route path="orders" element={<MyOrders />} />
                  <Route path="orders/:id" element={<OrderPage initialMode="view" />} />
                  <Route path="orders/:id/edit" element={<OrderPage initialMode="edit" />} />
                  <Route path="dashboard" element={<MyDashboard />} />
                  <Route path="settings" element={<MySettings />} />
                  <Route path="hires" element={<MyHires />} />
                  <Route path="grading" element={<Grading />} />
                  <Route path="approvals" element={<Approvals />} />
                  <Route path="department/orders" element={<DepartmentRoute allowStores><DepartmentOrders /></DepartmentRoute>} />
                  <Route path="department/hires" element={<DepartmentRoute><DepartmentHires /></DepartmentRoute>} />
                  <Route path="department/dashboard" element={<DepartmentRoute capability="canViewDepartmentDashboard"><DepartmentDashboard /></DepartmentRoute>} />
                  <Route path="admin/hires" element={<AdminRoute><AdminHires /></AdminRoute>} />
                  <Route path="admin/orders" element={<Navigate to="/department/orders" replace />} />
                  <Route path="admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                  <Route path="admin/settings" element={<BuyerRoute><AdminSettings /></BuyerRoute>} />
                  <Route path="admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
                  <Route path="budget/overview" element={<BudgetRoute><BudgetOverview /></BudgetRoute>} />
                  <Route path="budget/manage-budgets" element={<BudgetRoute><BudgetCostCodes /></BudgetRoute>} />
                  <Route path="hireforge" element={<MarketplaceBrowse />} />
                  <Route path="hireforge/listings/new" element={<MarketplaceCreateListing />} />
                  <Route path="hireforge/listings/:id" element={<MarketplaceListingDetail />} />
                  <Route path="hireforge/listings/:id/edit" element={<MarketplaceCreateListing />} />
                  <Route path="hireforge/my-listings" element={<MarketplaceMyListings />} />
                  {/* Section front door — Breakdown is now the Planning landing
                      (Overview removed, Round 8). */}
                  <Route path="teams" element={<Navigate to="/teams/breakdown" replace />} />
                  {/* 🔻 Gags/teams/tasks Stage 1 (2026-10-02): Manage Teams and the
                      team board are HIDDEN — both routes land on My Tasks. The
                      page files stay until Stage 2 deletes the old team code. */}
                  <Route path="teams/manage" element={<Navigate to="/teams/my-tasks" replace />} />
                  <Route path="teams/my-tasks" element={<MyTasks />} />
                  {/* Planning → Timesheets (S3b): every member of a real department; the page picks the screen by canManageBudget. */}
                  <Route path="timesheets" element={<Timesheets />} />
                  {/* Must stay above teams/:teamId — the param route would capture "breakdown" */}
                  <Route path="teams/breakdown" element={<GagBreakdown />} />
                  <Route path="teams/:teamId" element={<Navigate to="/teams/my-tasks" replace />} />
                  <Route path="risk-assessments" element={<RiskAssessmentOverview />} />
                  <Route path="risk-assessments/library" element={<RiskAssessmentLibrary />} />
                  <Route path="risk-assessments/library/blocks/edit/:kind/:id" element={<RiskAssessmentBlockEditor />} />
                  <Route path="risk-assessments/library/blocks/new" element={<RiskAssessmentBlockEditor />} />
                  {/* Admin-only CrewForge seed-block authoring (Phase 3) */}
                  <Route path="risk-assessments/library/seed-blocks/new" element={<RiskAssessmentBlockEditor seedMode />} />
                  <Route path="risk-assessments/library/seed-blocks/edit/:id" element={<RiskAssessmentBlockEditor seedMode />} />
                  <Route path="risk-assessments/library/templates/edit/fork/:id" element={<RiskAssessmentTemplateEditor />} />
                  {/* Admin-only CrewForge seed-template authoring (Phase 3) */}
                  <Route path="risk-assessments/library/seed-templates/new" element={<RiskAssessmentTemplateEditor seedMode />} />
                  <Route path="risk-assessments/library/seed-templates/edit/:id" element={<RiskAssessmentTemplateEditor seedMode />} />
                  <Route path="risk-assessments/new" element={<RiskAssessmentBuilder />} />
                  <Route path="risk-assessments/:id" element={<RiskAssessmentDetail />} />
                  {/* Legacy redirects — Overview is gone; point at Breakdown. */}
                  <Route path="team" element={<Navigate to="/teams/breakdown" replace />} />
                  <Route path="team/manage" element={<Navigate to="/teams/manage" replace />} />
                  <Route path="team/overview" element={<Navigate to="/teams/breakdown" replace />} />
                  <Route path="*" element={<DashboardRedirect />} />
                </Route>
                {/* Chrome-less pop-out: shares the auth + dept + page-header
                    context above but renders OUTSIDE the Layout wrapper so
                    there's no sidebar / header / nav — just the variance
                    view + a Download/Print button. Intended to be opened in
                    its own browser tab and dragged onto a second monitor. */}
                <Route path="budget/compare" element={<BudgetRoute><BudgetComparePopout /></BudgetRoute>} />
              </Routes>
              </PageHeaderProvider>
            </RequirePasswordChange>
            </DepartmentProvider>
          </RequireAuth>
        } />
      </Routes>
      <CookieConsentBanner />
      </ToastProvider>
      </AuthProvider>
  );
}

export const APP_ROUTE_OBJECTS = [
  { path: '*', element: <AppRoutes />, errorElement: <RethrowRouteError /> },
];

let appRouter = null;
export function getAppRouter() {
  if (!appRouter) appRouter = createBrowserRouter(APP_ROUTE_OBJECTS);
  return appRouter;
}

function App() {
  return (
    <ErrorBoundary>
      <RouterProvider router={getAppRouter()} />
    </ErrorBoundary>
  );
}

export default App;
